import React, {useEffect, useState} from 'react';
import {SOCIAL_AUTH_PROVIDER_GOOGLE} from "../../../../../common/constants/SocialAuthProvider";
import {useDispatch, useSelector} from "react-redux";
import {TimeOffset} from "../../../form/helpers/TimeHelper";
import {socialAuth} from "../../actions/UserActions";

const GoogleSignInButton = () => {
    const dispatch = useDispatch();
    const {signUpEnabled, socialAuthStatusGoogle, googleAppId, parentSite} = useSelector(state => state.site);
    const authTab = useSelector(state => state.authTab.authTab);
    const summary = useSelector(state => state.summary);
    const [googleReady, setGoogleReady] = useState(false);

    if (!socialAuthStatusGoogle) {
        return null;
    }

    useEffect(() => {
        const parseJwt = (token) => {
            var base64Url = token.split('.')[1];
            var base64 = decodeURIComponent(atob(base64Url).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(base64);
        };

        const prepareAuthData = (provider, accessToken, name, id, data, email) => {
            let authData = {
                'provider': provider,
                'access_token': accessToken,
                'name': name,
                'social_id': id,
                'user_time_offset': TimeOffset(),
                'additional_data': data,
            };
            if (typeof email !== undefined) {
                authData.email = email;
            }
            return authData;
        };

        const prepareGoogleAuthData = (profile, authResponse) => {
            return prepareAuthData(
                SOCIAL_AUTH_PROVIDER_GOOGLE,
                authResponse.credential,
                profile.name,
                profile.sub,
                {'profile': profile, 'authResponse': authResponse},
                profile.email,
            );
        };

        const parentSiteUrl = parentSite
            ? (parentSite.startsWith('http') ? parentSite : 'https://' + parentSite)
            : '';
        const isSatellite = parentSiteUrl && parentSiteUrl !== (window.location.protocol + '//' + window.location.host);

        if (isSatellite) {
            // Relay through parent domain (registered Google OAuth origin)
            const onRelayMessage = (e) => {
                if (e.origin.toLowerCase() !== parentSiteUrl.toLowerCase()) return;
                if (!e.data || e.data.type !== 'google-credential') return;
                const profile = parseJwt(e.data.credential);
                dispatch(socialAuth(prepareGoogleAuthData(profile, {credential: e.data.credential}))).then(() => {
                    if (!summary) window.location = '/account/';
                });
            };
            window.addEventListener('message', onRelayMessage);

            window.handleGoogleLogin = () => {
                const w = 500, h = 600;
                const l = Math.round(window.screenX + (window.outerWidth - w) / 2);
                const t = Math.round(window.screenY + (window.outerHeight - h) / 2);
                const relayUrl = parentSiteUrl + '/google-relay.php'
                    + '?client_id=' + encodeURIComponent(googleAppId)
                    + '&origin=' + encodeURIComponent(window.location.origin);
                window.open(relayUrl, 'google-relay', `width=${w},height=${h},left=${l},top=${t},resizable=yes`);
            };
            setGoogleReady(true);

            return () => {
                window.removeEventListener('message', onRelayMessage);
                delete window.handleGoogleLogin;
            };
        }

        const googleLoginCallback = (authResponse) => {
            let profile = parseJwt(authResponse.credential),
                authData = prepareGoogleAuthData(profile, authResponse);
            dispatch(socialAuth(authData)).then(() => {
                if (!summary) {
                    window.location = '/account/';
                }
            });
        };

        const initGoogleSignIn = () => {
            // Patch window.open so Google's popup is always centered, regardless
            // of where the fake button element lands in the DOM.
            const _winOpen = window.open.bind(window);
            window.open = function (url, name, features) {
                if (typeof url === 'string' && url.includes('accounts.google.com') && typeof features === 'string') {
                    const w = 500, h = 600;
                    const l = Math.round(window.screenX + (window.outerWidth - w) / 2);
                    const t = Math.round(window.screenY + (window.outerHeight - h) / 2);
                    features = features
                        .replace(/left=[-\d]+/i, 'left=' + l)
                        .replace(/top=[-\d]+/i,  'top='  + t);
                }
                return _winOpen(url, name, features);
            };

            google.accounts.id.initialize({
                client_id: googleAppId,
                ux_mode: "popup",
                callback: googleLoginCallback,
            });

            const createFakeGoogleWrapper = () => {
                const googleLoginWrapper = document.createElement("div");
                googleLoginWrapper.style.position = "fixed";
                googleLoginWrapper.style.opacity = "0";
                googleLoginWrapper.style.pointerEvents = "none";
                googleLoginWrapper.classList.add("custom-google-button");
                document.body.appendChild(googleLoginWrapper);

                google.accounts.id.renderButton(googleLoginWrapper, {
                    type: "icon",
                });

                const googleLoginWrapperButton = googleLoginWrapper.querySelector("div[role=button]");

                return {
                    click: () => {
                        // Use viewport center directly — CSS percentages break when
                        // an ancestor has transform/filter (creates new fixed ctx).
                        const cx = window.innerWidth / 2;
                        const cy = window.innerHeight / 2;
                        googleLoginWrapper.style.left = cx + 'px';
                        googleLoginWrapper.style.top = cy + 'px';
                        googleLoginWrapper.style.transform = 'translate(-50%, -50%)';
                        googleLoginWrapperButton.dispatchEvent(new MouseEvent('click', {
                            bubbles: true,
                            cancelable: true,
                            view: window,
                            clientX: cx,
                            clientY: cy,
                            screenX: window.screenX + cx,
                            screenY: window.screenY + cy,
                        }));
                    },
                };
            };

            const googleButtonWrapper = createFakeGoogleWrapper();

            window.handleGoogleLogin = () => {
                // Use wrapper click to prevent Illegal invocation exception
                googleButtonWrapper.click();
            };
            setGoogleReady(true);
        };

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initGoogleSignIn; // Call initGoogleSignIn when the script is loaded
        document.body.appendChild(script);

        return () => {
            delete window.handleGoogleLogin;
        };
    }, []);

    return (
        <div className="ab-google">
            <button className="auth-button-google" disabled={!googleReady} onClick={(event) => {
                event.preventDefault();
                window.handleGoogleLogin();
            }}>
                <img src="/_core/assets/themes/v5/assets/img/v3/icon-google.svg" alt="sign in google icon"/>
                {!signUpEnabled ? 'Sign in with Google' :
                    (authTab === 0 ? 'Sign up with Google' : 'Sign in with Google')}
            </button>
        </div>
    );
};

export default GoogleSignInButton;
